import { Maybe } from 'graphql/jsutils/Maybe'
import type { NextPage } from 'next'
import { CURRENTLY_FUNDING_INVESTMENT_BANNER_ID } from '@/constants/funding'
import { paths } from '@/constants/paths'
import { LandingProvider } from '@/contexts/LandingContext'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { Seo } from '@/layout/Seo'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getCollection, GUILD_MEMBERSHIP_NUMBERS_ID, WATCH_FOR_FREE_ENTRY_ID } from '@/services/CmsService/Collection'
import { getInvestmentBanner, InvestmentBannerResult } from '@/services/CmsService/InvestmentBanner'
import { getPageByPath } from '@/services/CmsService/Pages'
import { getGuildHomeHero } from '@/services/LandingPageService'
import { fetchPageDataContext, PageDataContext, PageModel } from '@/services/RenderService'
import { Collection, GuildHomeHero } from '@/types/codegen-contentful'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { determinePageRevalidation } from '@/utils/nextUtils/revalidation'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { LandingView } from '@/views/LandingView'

interface HomeProps {
  locale: string
  preview: boolean
  guildHomeHero: GuildHomeHero | null
  investmentBanner: InvestmentBannerResult | null
  watchForFree: Collection | null
  members?: number
  lastUpdated?: string
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
}

const Home: NextPage<HomeProps> = ({
  locale,
  preview,
  investmentBanner,
  guildHomeHero,
  watchForFree,
  members,
  lastUpdated,
  pageDataContext,
  page,
}) => {
  const { t } = useTranslate('home')
  const url = `${getBasePath(locale)}${paths.home}`
  const title = t('angelHomeMetaTitleV2', 'Angel: Stream Original, Uplifting Shows for the Whole Family')
  const description = t(
    'angelStudiosHomeMetaDescriptionV2',
    'Join the Guild to get unlimited access to films and TV series that amplify light through high-quality, fan-supported entertainment.',
  )

  return (
    <>
      <Seo
        canonical={url}
        title={title}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.index}
      />
      <LandingProvider
        guildHomeHero={guildHomeHero}
        watchForFree={watchForFree}
        members={members}
        lastUpdated={lastUpdated}
      >
        <ThemeContextProvider isDarkMode>
          <MarketingVideoPopup />
          <LandingView
            investmentBanner={investmentBanner}
            preview={preview}
            pageDataContext={pageDataContext}
            page={page}
          />
        </ThemeContextProvider>
      </LandingProvider>
    </>
  )
}

export const getStaticProps = getStaticPropsErrorHandler<HomeProps>(
  { filePath: 'pages/[locale]/home/logged-in.tsx' },
  async ({ locale, preview, federationClient }) => {
    const [guildHomeHero, investmentBanner, watchForFree, guildMembershipNumbers, page, translations] =
      await Promise.all([
        getGuildHomeHero({ locale, preview }),
        getInvestmentBanner({ locale, preview }, CURRENTLY_FUNDING_INVESTMENT_BANNER_ID),
        getCollection({ locale, preview }, WATCH_FOR_FREE_ENTRY_ID),
        getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID),
        getPageByPath(paths.home, { locale, preview }),
        loadTranslations(locale, ['common', 'home', 'app-promo', 'account', 'guild', 'watch']),
      ])

    const pageDataContext = page ? await fetchPageDataContext(page, preview, locale, federationClient) : null

    const members = Number(guildMembershipNumbers?.customData[0]?.numSubscribers.replace(/,/g, '')) ?? null
    const lastUpdated = guildMembershipNumbers?.customData[0]?.updatedAt ?? null

    return {
      props: {
        locale,
        preview,
        guildHomeHero: guildHomeHero || null,
        investmentBanner: investmentBanner || null,
        watchForFree: watchForFree || null,
        page,
        pageDataContext,
        members,
        lastUpdated,
        ...translations,
      },
      revalidate: determinePageRevalidation(page, pageDataContext),
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default Home
